globalThis.DsCommon = globalThis.DsCommon || {

    currentUser: null,
    cachedData: {},

    loadCachedData: function (getUrl, callback, adjustCachedResponse) {
        var data = DsCommon.cachedData[getUrl];
        var request = null;

        // already requested or resolved?
        if (data != null) {
            // A request that is still in flight is stored here as a Promise, so the
            // test has to be for a thenable. `typeof somePromise` is "object", never
            // "function", so the old `typeof data === "function"` check never matched:
            // every caller that arrived while a request was in flight fell through to
            // the "already resolved" branch and was handed the PROMISE ITSELF as its
            // data. For /license/ that made `license.isVerified` undefined, and a
            // verified account got redirected to /error/notverified.
            if (typeof data.then === "function") {
                // pending request - wait for it, then hand over the real data
                request = data.then(function (data) {
                    if (data != null) {
                        callback(data);
                    }
                    return data;
                });
            } else {
                // already resolved
                callback(data);
                return Promise.resolve(data);
            }
        } else {
            // no, this is the first request
            DsCommon.cachedData[getUrl] = request = fetch(getUrl, { method: 'GET' })
                .then(response => response.json())
                .then(function (data) {
                    var data = adjustCachedResponse != null ? adjustCachedResponse(data) : data;
                    if (data != null) {
                        // Cache the result
                        DsCommon.cachedData[getUrl] = data;

                        // Execute the callback
                        callback(data);
                    }

                    return data;
                })
                .catch(function (e) {
                    // Drop the failed entry, otherwise the rejected Promise stays in
                    // the cache forever and every later caller on this page gets it
                    // back instead of data. Rethrow so behaviour is unchanged for any
                    // caller that attaches its own handler.
                    delete DsCommon.cachedData[getUrl];
                    throw e;
                });
        }

        return request;
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads the tenant's subscription preferences						
    // └────────────────────────────────────────────────────────────────────
    loadSubscriptionPreferences: function (callback) {
        return DsCommon.loadCachedData(`/api/v1/preferences/`, callback, json => {
            if (json.Status === true) {
                return json.Response.preferences;
            } else {
                // changes were not saved
                DsUtility.notificationMessage(false, json.Message);
                return null;
            }
        });
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads the tenant's license						
    // └────────────────────────────────────────────────────────────────────
    loadLicense: function (callback, allowCache = true) {
        return DsCommon.loadCachedData(`/license/?allowCache=${allowCache}`, callback);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads users for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadUsers: function(callback) {
        return DsCommon.loadCachedData('/api/v1/users', callback, json => {
            if (json.Response.current) {
                DsCommon.currentUser = json.Response.current.uniqueId;
            }

            var users = json.Response.users.map(u => ({
                fullname: u.fullname,
                id: u.uniqueId
            }));
            users.sort((a, b) => a.fullname.localeCompare(b.fullname));

            return users;
        });
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads tags for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadTags: function (callback) {
        return DsCommon.loadCachedData('/api/v1/tags/', callback, json => json.Response.tags);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads tags for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadTrackingLinks: function (callback) {
        return DsCommon.loadCachedData('/api/v1/trackinglinks/', callback, json => json.Response.trackinglinks);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads text message keywords for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadTextMessageKeywords: function (callback) {
        return DsCommon.loadCachedData('/api/v1/textmessage/keywords', callback, json => json.Response.keywords);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads popups for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadPopups: function (callback) {
        return DsCommon.loadCachedData('/api/v1/popups/', callback, json => json.Response.popups);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads emails for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadEmails: function (callback) {
        return DsCommon.loadCachedData('/Email/Dropdownlist', callback);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads text messages for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadTextMessages: function (callback) {
        return DsCommon.loadCachedData('/Sms/Dropdownlist', callback);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads segments for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadSegments: function (callback) {
        return DsCommon.loadCachedData('/Segment/Dropdownlist', callback);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads events for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadContactEvents: function (callback) {
        return DsCommon.loadCachedData('/Event/Dropdownlist', callback);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads automations for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadAutomations: function (callback) {
        return DsCommon.loadCachedData('/Autopilot/Dropdownlist', callback);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads webforms for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadWebforms: function (callback, id = null) {
        return DsCommon.loadCachedData(id == null ? '/api/v1/webforms/' : `/api/v1/webforms/${id}`, callback, json => json.Response.webforms);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads magicforms for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadMagicforms: function (callback) {
        return DsCommon.loadCachedData('/api/v1/magicforms/', callback, json => json.Response.magicforms);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads active campaigns for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadCampaigns: function (callback) {
        return DsCommon.loadCachedData('/api/v1/campaigns', callback, json => json.Response.campaigns);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads active campaigns for current tenant
    // └────────────────────────────────────────────────────────────────────
    loadAllCampaigns: function (callback) {
        return DsCommon.loadCachedData('/api/v1/campaigns/all', callback, json => json.Response.campaigns);
    },


    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads a specific file from File Manager the callback returns the file
    // └────────────────────────────────────────────────────────────────────
    loadFile: function (fileId, callback) {

        if (undefined === fileId) {
            console.error("A file id is required.");
            return;
        }

        var $request = $.ajax({
            url: `/filemanager/file/${fileId}`
        });

        $request.then(function (data) {

            // Get the results of all users
            var results = data.Response.file;

            callback(results);
        });
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads file manager folders for current tenant - the callback is passed
    // │ back a list of the folders
    // └────────────────────────────────────────────────────────────────────
    loadFolders: function (folderId, callback) {

        if (undefined === folderId)
            folderId = 0;

        var $request = $.ajax({
            url: `/api/v1/filemanager/folders/${folderId}`
        });

        $request.then(function (data) {

            // Get the results of all users
            var results = data.Response.folders;
            var folders = [];

            for (var i = 0; i < results.length; i++) {

                folders.push({
                    "name": results[i].name,
                    "folderId": results[i].folderId,
                    "fileCount": results[i].fileCount,
                    "hasChildFolders": results[i].hasChildFolders
                });
            }

            callback(folders);
        });

    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Loads file manager folders breadcrumbs, walks backwards returns parents
    // | to root.
    // └────────────────────────────────────────────────────────────────────
    loadFolderParents: function (folderId, callback) {

        if (undefined === folderId)
            folderId = 0;

        var $request = $.ajax({
            url: `/filemanager/breadcrumbs/${folderId}`
        });

        $request.then(function (data) {

            // Get the results of all users
            var results = data.Response.breadcrumbs;
            var parents = [];
            var currentFolder = null;

            for (var i = 0; i < results.length; i++) {

                if (results[i].folderId == folderId)
                    currentFolder = results[i];

                parents.push({
                    "name": results[i].name,
                    "folderId": results[i].folderId
                });
            }

            callback(currentFolder, parents);
        });

    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Common event handler for delete click handler
    // └────────────────────────────────────────────────────────────────────
    eventDeleteButton: function (target, api_path, redirect_path, message) {

        if (!target || !api_path || !redirect_path)
            return;

        target.addEventListener('click', (e) => {
            // Confirm delete
            if (e.currentTarget.innerText !== 'Really?') {
                e.currentTarget.innerText = 'Really?';

                return;
            }

            DsUtility.pleaseWaitOn("Deleting...");

            fetch(api_path, {
                method: "DELETE"
            }).then(data => {
                return data;
            }).then(res => {
                DsUtility.pleaseWaitOff(1000, () => {

                    if (message) {
                        DsUtility.notificationMessage(true, message);
                    }

                    window.location = redirect_path || DsUtility.getHistory();
                });
            });
        });

        // ┌────────────────────────────────────────────────────────────────────
        // │ Resets the delete button
        // └────────────────────────────────────────────────────────────────────
        target.addEventListener('mouseout', (e) => {
            e.currentTarget.innerText = 'Delete';
        });
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Common event handler for archive click handler
    // └────────────────────────────────────────────────────────────────────
    eventArchiveButton: function (target, api_path, redirect_path, message) {

        if (!target || !api_path || !redirect_path)
            return;

        target.addEventListener('click', (e) => {
            // Confirm archive
            if (e.currentTarget.innerText.trim() !== 'Really?') {
                e.currentTarget.innerHTML = 'Really?';

                return;
            }

            DsUtility.pleaseWaitOn("Archiving...");

            fetch(api_path, {
                method: "POST"
            }).then(data => {
                return data;
            }).then(res => {
                DsUtility.pleaseWaitOff(1000, () => {

                    if (message) {
                        DsUtility.notificationMessage(true, message);
                    }

                    window.location = redirect_path || DsUtility.getHistory();
                });
            });
        });

        // ┌────────────────────────────────────────────────────────────────────
        // │ Resets the archive button
        // └────────────────────────────────────────────────────────────────────
        target.addEventListener('mouseout', (e) => {
            e.currentTarget.innerHTML = 'Archive';
        });
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Common event handler for copy click handler
    // └────────────────────────────────────────────────────────────────────
    eventCopyButton: function (target, api_path, redirect_path, message) {

        if (!target || !api_path || !redirect_path)
            return;

        target.addEventListener('click', (e) => {
            DsUtility.pleaseWaitOn("Copying...");

            fetch(api_path, {
                method: "POST"
            }).then(data => {
                return data.json();
            }).then(json => {

                let i = redirect_path.indexOf('?');
                let qs = '';

                // do we have querystring params
                if (i > 0) {
                    qs = redirect_path.substring(i);
                    redirect_path = redirect_path.substring(0, i);
                }
                if (message) {
                    DsUtility.notificationMessage(true, message);
                }
                // reassmble url
                if (redirect_path.includes("[id]")) {
                    redirect_path = redirect_path.replace("[id]", json.Response.id);
                    window.location = `${redirect_path.trimEnd('/')}/${qs}`;
                } else {
                    window.location = `${redirect_path.trimEnd('/')}/${json.Response.id}${qs}`;
                }

            });
        });
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Common event handler for patch click handler
    // └────────────────────────────────────────────────────────────────────
    eventPatchButton: function (target, api_path, elem, please_wait = 'Updating...') {
        DsCommon.eventButton(target, api_path, elem, 'PATCH', please_wait);
    },

    // ┌────────────────────────────────────────────────────────────────────
    // │ Common event handler for patch click handler
    // └────────────────────────────────────────────────────────────────────
    eventButton: function (target, api_path, elem, verb = "POST", please_wait = 'Updating...') {

        if (!target || !api_path)
            return;

        target.style.minWidth = target.offsetWidth + 'px';

        target.addEventListener('click', (e) => {
            // Confirm delete
            if (e.currentTarget.innerText !== 'Really?') {
                e.currentTarget.innerText = 'Really?';

                return;
            }

            DsUtility.pleaseWaitOn(please_wait ? please_wait : 'Updating...');

            fetch(api_path, {
                method: verb,
                body: elem ? JSON.stringify(elem.value) : null
            }).then(data => {
                return data.json();
            }).then(res => {
                e.target.innerText = e.target.dataset.default;

                if (res.Status) {
                    DsUtility.notificationMessage(true, res.Message);
                } else {
                    DsUtility.notificationMessage(false, res.Message);
                }
                DsUtility.pleaseWaitOff(1000);
            });
        });

        // ┌────────────────────────────────────────────────────────────────────
        // │ Resets the delete button
        // └────────────────────────────────────────────────────────────────────
        target.addEventListener('mouseout', (e) => {
            e.target.innerText = e.target.dataset.default;
        });
    },

};